import React, {
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import styled from "styled-components";
import clsx from "clsx";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Drawer,
  List,
  Divider,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  ListSubheader,
} from "@material-ui/core";

import { CreditsIcon, ReferencesIcon, QuoteIcon } from "./icons";

import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import Close from "@material-ui/icons/CancelRounded";
import LinkIcon from "@material-ui/icons/OpenInNewRounded";

import RoundButton from "../RoundButton";

import references from "./references";
import credits from "./credits";
import comocitar from "./citar";

import theme from "../../theme";

const useStyles = makeStyles({
  list: {
    width: 250,
    height: "100vh",
    "@media(min-width: 900px)": {
      minWidth: 368,
    },
    overflow: "hidden",
  },
  fullList: {
    width: "auto",
  },
  icons: {
    color: "white",
    fontSize: "2.5rem",
    margin: "0 10px",
    "@media(min-width: 900px)": {
      fontSize: "3rem",
      margin: "0.5rem",
    },

    "@media(min-width: 1800px)": {
      fontSize: "3.5rem",
    },
  },
  ListItem: {
    borderRadius: "10px",
    width: "95%",
    marginLeft: "2.5%",
    marginRight: "2.5%",
    transition: "0.2s ease",

    "&:hover": {
      transition: "0.2s ease",
      backgroundColor: theme.palette.drawer.light,
      color: theme.palette.drawer.dark,
      fill: theme.palette.drawer.dark,
    },

    "&:focus": {
      transition: "0.2s ease",
      backgroundColor: theme.palette.drawer.light,
      color: theme.palette.drawer.dark,
    },
  },
  ListItemText: {
    borderRadius: "10px",
    width: "95%",
    margin: "0 2.5%",
    transition: "0.2s ease",
    marginBottom: "0.25rem",
    p: {
      fontWeight: 900,
    },
    "&:focus": {
      transition: "0.2s ease",
      backgroundColor: theme.palette.drawer.light,
      color: theme.palette.drawer.dark,
    },
  },
  ListTitle: {
    background: theme.palette.drawer.light,
    color: theme.palette.drawer.dark,
  },
  ListTitleBox: {
    width: "95%",
    margin: "auto",
    "@media(min-width: 900px)": {
      width: "100%",
    },
  },
  ListItemActive: {
    // backgroundColor: theme.palette.primary.lighter,
    color: theme.palette.primary.darker,
    borderRadius: "10px",
    width: "95%",
    margin: "0px 2.5%",
    p: {
      fontWeight: "900",
    },
  },
  ListIcon: {
    fill: "#333",
    display: "flex",
    justifyContent: "center",
    "&:focus": {
      color: theme.palette.primary.darker,
    },
  },
  ListIconActive: {
    color: theme.palette.primary.darker,
    display: "flex",
    justifyContent: "flex-start",
  },
  RefLink: {
    opacity: 0.9,
    lineHeight: 1,
  },
  CreditOccupation: {
    textTransform: "uppercase",
    lineHeight: 1.1,
    margin: ".75rem 0",
    fontSize: "1.1rem",
    "@media(min-width: 1200px)": {
      fontSize: "1.2rem",
    },
    "@media(min-width: 1800px)": {
      fontSize: "1.4rem",
    },
  },
  ItemName: {
    fontSize: "1.1rem",
    "@media(min-width: 1200px)": {
      fontSize: "1.2rem",
    },
    "@media(min-width: 1800px)": {
      fontSize: "1.3rem",
    },
  },
  ItemTitle: {
    fontSize: "1.1rem",
    "@media(min-width: 1200px)": {
      fontSize: "1.2rem",
    },
    "@media(min-width: 1800px)": {
      fontSize: "1.3rem",
    },
    fontWeight: 'bolder',
  },
  ContentList: {
    height: "90vh",
    overflow: "auto",
    "&::-webkit-scrollbar": {
      width: "8px",
      "@media(min-width: 900px)": {
        width: "8px",
      },
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: theme.palette.drawer.dark,
      borderRadius: "10px",
      border: "2px solid white",
      "-webkit-box-shadow": "inset 0 0 6px rgba(0, 0, 0, 0.5)",
    },
  },
  ArrowBack: {
    cursor: "pointer",
  },
  Title: {
    fontWeight: "900",
  },
  ActiveIcon: {
    color: theme.palette.drawer.MainMenuCloseBtnColor,
  },
  HasLink: {
    height: "fit-content",
    margin: "0",
    padding: "0",
    paddingBottom: {
      lineHeight: 1,
      margin: "0",
      padding: "0",
    },
  },
  Link: {
    color: theme.palette.drawer.dark,
    lineHeight: 1,
    margin: "0",
    padding: "0",
    fontSize: "0.9rem",
    "@media(min-width: 1200px)": {
      fontSize: "1.1rem",
    },
  },
  ItemLink: {
    marginTop: ".25rem",
  },
  IconWrapper: {
    width: "1.8rem",
    overflow: "hidden",
  },
  refNumber: {
    background: theme.palette.drawer.dark,
    color: theme.palette.background.main,
    padding: ".05rem .25rem",
    borderRadius: ".25rem",
  },
  copyright: {
    margin: ".75rem 2.5%",
    paddingTop: "3rem",
  },
});

const TopBox = styled.div`
  display: flex;
  align-items: center;
  height: 3rem;
  padding: 0 1rem;

  @media (max-height: 500px) {
    height: 40px;
  }

  @media (min-width: 600px) {
    min-height: 56px;
  }

  @media (min-width: 900px) {
    height: 3.5rem;
    padding: 0 1.75rem;
  }

  @media (min-width: 900px) and (min-height: 900px) {
    height: 4rem;
  }
`;

const InfoDrawer = (props, ref) => {
  const classes = useStyles();
  const [opened, setOpened] = useState(false);
  const [menu, setMenu] = useState(true);
  const [refs, setRefs] = useState(false);
  const [creds, setCreds] = useState(false);
  const [citar, setCitar] = useState(false);
  const [selected, setSelected] = useState(0);
  const listRef = useRef();

  const toggleDrawer = (open, select) => (event) => {
    if (
      event.type === "keydown" &&
      (event.key === "Tab" || event.key === "Shift")
    ) {
      return;
    }

    setOpened(open);
    setSelected(select);
  };

  const open = (option) => {
    if (option === "menu") {
      setRefs(false);
      setCreds(false);
      setMenu(true);
      setCitar(false);
    }
    if (option === "refs") {
      setMenu(false);
      setRefs(true);
      setCreds(false);
      setCitar(false);
    }
    if (option === "creds") {
      setMenu(false);
      setRefs(false);
      setCreds(true);
      setCitar(false);
    }
    if (option === "citar") {
      setMenu(false);
      setRefs(false);
      setCitar(true);
      setCreds(false);
    }
    if (option === "imagens") {
      setMenu(false);
      setRefs(false);
      setCitar(false);
      setCreds(false);
    }
  };

  useImperativeHandle(ref, () => ({
    openDrawer: (key) => {
      setOpened(true);
      setSelected(key);
      open("refs");
    },
    scrollList: () => {
      if (listRef.current) {
        if (listRef.current.childNodes[selected - 1]) {
          listRef.current.childNodes[selected - 1].scrollIntoView();
        }
      }
    },
  }));

  const menuList = (anchor) => (
    <div
      className={clsx(classes.list, {
        [classes.fullList]: anchor === "top" || anchor === "bottom",
      })}
      role='presentation'
      onKeyDown={toggleDrawer(false)}
    >
      <List className={classes.ListTitle}>
        <TopBox className={classes.ListTitleBox}>
          <ListItemIcon className={classes.ArrowBack}>
            <Close
              onClick={toggleDrawer(false)}
              className={classes.ActiveIcon}
            />
          </ListItemIcon>
          <Typography variant='body1' className={classes.Title}>
            MENU
          </Typography>
        </TopBox>
      </List>
      <List>
        <ListItem
          className={classes.ListItem}
          button
          key={1}
          onClick={() => open("creds")}
        >
          <ListItemIcon className={classes.ListIconActive}>
            <Box className={classes.IconWrapper}>
              {/* AQUI VOCÊ MUDA A COR DO ICONES DO DRAWER */}
              <CreditsIcon fill={theme.palette.drawer.MainMenuIconColor} />
            </Box>
          </ListItemIcon>
          <ListItemText disableTypography>
            <Typography variant='body1'>Créditos</Typography>
          </ListItemText>
        </ListItem>
      </List>
      <List>
        <ListItem
          className={classes.ListItem}
          button
          key={2}
          onClick={() => open("refs")}
        >
          <ListItemIcon className={classes.ListIconActive}>
            <Box className={classes.IconWrapper}>
              {/* AQUI VOCÊ MUDA A COR DO ICONES DO DRAWER */}
              <ReferencesIcon fill={theme.palette.drawer.MainMenuIconColor} />
            </Box>
          </ListItemIcon>
          <ListItemText disableTypography>
            <Typography variant='body1'>Referências</Typography>
          </ListItemText>
        </ListItem>
      </List>
      <List>
        <ListItem
          className={classes.ListItem}
          button
          key={2}
          onClick={() => open("citar")}
        >
          <ListItemIcon className={classes.ListIconActive}>
            <Box className={classes.IconWrapper}>
              {/* AQUI VOCÊ MUDA A COR DO ICONES DO DRAWER */}
              <QuoteIcon fill={theme.palette.drawer.MainMenuIconColor} />
            </Box>
          </ListItemIcon>
          <ListItemText disableTypography>
            <Typography variant='body1'>Como citar este material</Typography>
          </ListItemText>
        </ListItem>
      </List>
    </div>
  );

  const refsList = (anchor) => {
    return (
      <div
        className={clsx(classes.list, {
          [classes.fullList]: anchor === "top" || anchor === "bottom",
        })}
        role='presentation'
        onKeyDown={toggleDrawer(false)}
      >
        <List className={classes.ListTitle}>
          <TopBox className={classes.ListTitleBox}>
            <ListItemIcon className={classes.ArrowBack}>
              <ArrowBackIcon
                onClick={() => open("menu")}
                className={classes.ActiveIcon}
              />
            </ListItemIcon>
            <Typography variant='body1' className={classes.Title}>
              REFERÊNCIAS
            </Typography>
          </TopBox>
        </List>
        {/* referências */}
        <List className={classes.ContentList} ref={listRef}>
          {references.map((ref, i) => {
            return (
              <ListItem key={i} selected={selected === i + 1}>
                <ListItemText disableTypography>
                  <Typography variant='body1' className={classes.ItemName}>
                    <strong>{i + 1}</strong>
                    {". " + ref.info}
                  </Typography>
                  {ref.link ? (
                    <Typography
                      variant='body2'
                      className={[classes.ItemLink, classes.ItemName]}
                    >
                      Disponível{" "}
                      <a
                        href={ref.link}
                        target='_blank'
                        rel='noopener noreferrer'
                        className={classes.Link}
                      >
                        <strong>clicando aqui</strong>
                      </a>
                    </Typography>
                  ) : (
                    <></>
                  )}
                </ListItemText>
              </ListItem>
            );
          })}
        </List>
      </div>
    );
  };

  const citarList = (anchor) => {
    return (
      <div
        className={clsx(classes.list, {
          [classes.fullList]: anchor === "top" || anchor === "bottom",
        })}
        role='presentation'
        onKeyDown={toggleDrawer(false)}
      >
        <List className={classes.ListTitle}>
          <TopBox className={classes.ListTitleBox}>
            <ListItemIcon className={classes.ArrowBack}>
              <ArrowBackIcon
                onClick={() => open("menu")}
                className={classes.ActiveIcon}
              />
            </ListItemIcon>
            <Typography variant='body1' className={classes.Title}>
              COMO CITAR ESTE MATERIAL
            </Typography>
          </TopBox>
        </List>
        {/* referências */}
        <List className={classes.ContentList} ref={listRef}>
          {comocitar.map((quote, i) => {
            return (
              <ListItem key={i} selected={selected === i + 1}>
                <ListItemText disableTypography>
                  <Typography variant='body1' className={classes.ItemName}>
                    {quote.text}
                  </Typography>
                </ListItemText>
              </ListItem>
            );
          })}
        </List>
      </div>
    );
  };

  const creditsList = (anchor) => {
    return (
      <div
        className={clsx(classes.list, {
          [classes.fullList]: anchor === "top" || anchor === "bottom",
        })}
        role='presentation'
        onKeyDown={toggleDrawer(false)}
      >
        <List className={classes.ListTitle}>
          <TopBox className={classes.ListTitleBox}>
            <ListItemIcon className={classes.ArrowBack}>
              <ArrowBackIcon
                onClick={() => open("menu")}
                className={classes.ActiveIcon}
              />
            </ListItemIcon>
            <Typography variant='body1' className={classes.Title}>
              CRÉDITOS
            </Typography>
          </TopBox>
        </List>
        {/* créditos */}
        <List className={classes.ContentList}>
          {credits.map((cred, i) => {
            return (
              <>
                <ListSubheader
                  className={classes.CreditOccupation}
                  disableSticky
                >
                  {cred.function}
                </ListSubheader>
                {cred.team ? (
                  <>
                    {cred.team.map((person, personIndex) => {
                      return (
                        <ListItem key={i} className={classes.CreditTeam}>
                          <ListItemText disableTypography>
                            <Typography
                              key={personIndex}
                              variant='body1'
                              className={classes.ItemName}
                            >
                              {person.name}
                            </Typography>
                            <Typography
                              key={personIndex}
                              variant='body1'
                              className={classes.ItemTitle}
                            >
                              {person.title}
                            </Typography>
                            {person.link ? (
                              <Box className={classes.HasLink}>
                                <Typography variant='body2'>
                                  <a
                                    href={person.link}
                                    className={classes.Link}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                  >
                                    Lattes <LinkIcon fontSize='small' />
                                  </a>
                                </Typography>
                              </Box>
                            ) : (
                              <>
                                
                              </>
                            )}
                          </ListItemText>
                        </ListItem>
                      );
                    })}
                  </>
                ) : (
                  <></>
                )}
                <Divider />
              </>
            );
          })}
          <div className={classes.copyright}>
            <Typography variant='body2'>
              © 2021. Ministério da Saúde. Sistema Universidade Aberta do SUS.
              Fundação Oswaldo Cruz & Universidade Federal do Maranhão. É
              permitida a reprodução, disseminação e utilização desta obra, em
              parte ou em sua totalidade, nos termos da licença para usuário
              final do Acervo de Recursos Educacionais em Saúde (ARES). Deve ser
              citada a fonte e é vedada sua utilização comercial, sem a
              autorização expressa dos seus autores, conf. Lei de Direitos
              Autorais-LDA (Lei n.⁰9.610, de 19 de fevereiro de 1998).
            </Typography>
          </div>
        </List>
      </div>
    );
  };

  return (
    <div>
      <>
        <RoundButton type='menu' onClick={toggleDrawer(true)} tooltip='Menu' />
        <Drawer anchor='left' open={opened} onClose={toggleDrawer(false)}>
          {menu ? menuList() : <></>}
          {refs ? refsList() : <></>}
          {creds ? creditsList() : <></>}
          {citar ? citarList() : <></>}
        </Drawer>
      </>
    </div>
  );
};

export default forwardRef(InfoDrawer);
